<template>
  <div>
    <v-sheet elevation="1">
      <v-toolbar dense color="white">
        <v-toolbar-title>{{ screenTitle }}</v-toolbar-title>
        <v-spacer></v-spacer>
      </v-toolbar>
      <v-container fluid>
        <error-message :error-object="errorObject" />
        <success-message :success-object="successObject" />
        <p style="margin: 5px 0 15px 0;">
          バス情報は下の表を参照してください。条件を指定することで一覧から絞り込むことができます。<br />
          チェックボックスで選択したバス情報のバスQRコードとバス車両一覧を印刷することができます。
        </p>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="vehicleNumberFilterValue"
              label="車番"
              :placeholder="config.PLACEHOLDER_INPUT"
              append-icon="mdi-magnify"
              clearable
              outlined
            >
            </v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="operatorFilterValue"
              :items="[...operators]"
              label="バス会社"
              item-text="vehicle_operator_name"
              item-value="vehicle_operator_id"
              :placeholder="config.PLACEHOLDER_SELECT"
              clearable
              outlined
            >
            </v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="issueDateFilterValue"
              :items="[...issueDates]"
              label="QRコード取得日"
              item-text="formatted_created_date"
              item-value="created_date"
              :placeholder="config.PLACEHOLDER_SELECT"
              clearable
              outlined
            ></v-select>
          </v-col>
        </v-row>
        <v-divider class="mb-1"></v-divider>
      </v-container>
      <top-operation-data-table
        v-model="selected"
        :headers="headers"
        :items="items"
        :sort-by="sort.by"
        item-key="vehicle_id"
        locale="ja-jp"
        loading-text="読込中"
        no-data-text="データがありません。"
        show-select
        must-sort
        class="mb-3"
      >
        <template v-slot:[`item.created_date`]="{ item }">
          {{ formatDate(item.created_date) }}
        </template>
      </top-operation-data-table>
      <v-row>
        <v-col>
          <v-btn
            :disabled="this.selected.length == 0"
            v-on:click="downloadPdf()"
            class="mx-2"
            color="primary"
            >バスQRコード印刷</v-btn
          >
          <v-btn
            :disabled="this.selected.length == 0"
            v-on:click="downloadTablePdf()"
            class="mx-2"
            color="primary"
            >バス車両一覧印刷</v-btn
          >
        </v-col>
      </v-row>
    </v-sheet>
    <v-overlay :value="overlay" opacity="1" color="white">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import { RepositoryFactory } from "@/js/repository/RepositoryFactory";
import { createBusQrCodePdf } from "@/js/repository/pdf-creator/busQrCodePdfCreator";
import { createBusTablePdf } from "@/js/repository/pdf-creator/busTablePdfCreator";
import { ScreenId } from "@/js/value-objects/screen-id";
import messageUtils from "@/mixins/message-utils";
import config from "@/js/const";

const busQrCodeRepository = RepositoryFactory.get("busQrCode");

export default {
  mixins: [messageUtils],

  components: {
    ErrorMessage: () => import("@/components/ErrorMessage"),
    SuccessMessage: () => import("@/components/SuccessMessage"),
    TopOperationDataTable: () => import("@/components/ui/TopOperationDataTable")
  },
  data: () => ({
    loading: false,
    overlay: false,
    screenTitle: "", // 画面名
    selected: [],
    items: [],
    operators: [], //会社名（検索項目のセレクトボックス用）
    issueDates: [],
    vehicleNumberFilterValue: "",
    operatorFilterValue: "",
    issueDateFilterValue: "",
    config: config // 共通プレースホルダ
  }),

  computed: {
    headers() {
      return [
        {
          text: "バスID",
          align: " d-none",
          value: "vehicle_id"
        },
        {
          text: "車番",
          value: "vehicle_number",
          filter: this.vehicleNumberFilter
        },
        {
          text: "バス会社ID",
          align: " d-none",
          value: "vehicle_operator_id",
          filter: this.operatorNameFilter
        },
        {
          text: "バス会社名",
          value: "vehicle_operator_name"
        },
        {
          text: "乗車可能人数",
          value: "vehicle_limit"
        },
        {
          text: "QRコード取得日",
          value: "created_date",
          filter: this.issueDateFilter
        },
        {
          text: "削除フラグ",
          align: " d-none",
          value: "delete_flg",
          filter: this.deleteFlgFilter
        }
      ];
    },
    sort() {
      /**
       * /一覧をソートした状態で一覧のチェックボックスにチェックを入れると
       * ソート状態が解除されるエラーに対応
       * */
      return {
        by: ["vehicle_number"]
      };
    }
  },

  created() {
    this.initialize();
  },

  methods: {
    async initialize() {
      this.overlay = true;
      this.loading = true;

      // タイトルとブラウザタブー画面名設定
      this.screenTitle = ScreenId.getScreenTitle(ScreenId.SF0610);
      this.pageTitle(this.screenTitle);

      await busQrCodeRepository
        .getList(ScreenId.SF0610)
        .then(response => {
          let busInfoList = response;
          this.items = busInfoList.data.results;

          busInfoList.data.results.forEach(value => {
            // issueDatesの作成
            this.issueDates.push({
              created_date: this.formatDate(value.created_date),
              formatted_created_date: this.formatDate(value.created_date)
            });
            // issueDatesの並び替え
            this.issueDates.sort(function(a, b) {
              if (a.created_date < b.created_date) return 1;
              if (a.created_date > b.created_date) return -1;
              return 0;
            });

            // operatorsの作成
            this.operators.push({
              vehicle_operator_id: value.vehicle_operator_id,
              vehicle_operator_name: value.vehicle_operator_name
            });
          });
        })
        .catch(e => {
          this.handleError(e, ScreenId.SF0610, "aggregation-menu");
        });
      this.overlay = false;
      this.loading = false;
    },

    formatDate(date) {
      if (!date) {
        return "";
      }
      const formattedDate = this.$moment(date);
      if (formattedDate.isValid()) {
        return formattedDate.format("YYYY/MM/DD");
      }
      return "";
    },

    // 車番の検査項目フィルター
    vehicleNumberFilter(value) {
      if (!this.vehicleNumberFilterValue) {
        return true;
      }
      return value.includes(this.vehicleNumberFilterValue);
    },

    // 会社名の検査項目フィルター
    operatorNameFilter(value) {
      if (!this.operatorFilterValue) {
        return true;
      }
      return value === this.operatorFilterValue;
    },

    // QRコードの取得日の検査項目フィルター
    issueDateFilter(value) {
      if (!this.issueDateFilterValue) {
        return true;
      }
      return this.formatDate(value) === this.issueDateFilterValue;
    },

    deleteFlgFilter(deleteFlg) {
      return !deleteFlg;
    },
    // 選択したバス情報をユニコード順にソートする
    unicodeSort() {
      const SELECTED_BUSIINFO = this.selected.sort((a, b) => {
        // vuetifyのユニコードソートが値の一番左の0を消しているので同じく実装
        return a.vehicle_number
          .replace(/^0+/, "")
          .localeCompare(b.vehicle_number.replace(/^0+/, ""));
      });
      return SELECTED_BUSIINFO;
    },
    // バスQRコード印刷
    downloadPdf() {
      createBusQrCodePdf(this.unicodeSort());
    },

    // バス車両一覧印刷
    downloadTablePdf() {
      createBusTablePdf(this.unicodeSort());
    }
  }
};
</script>
